import { Loader2, Plus } from "lucide-react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { usePaymentMethods } from "@/api/payment-query";
import { useCreateDesposit, useUpdateTransaction, useVerifyVfdSession } from "@/api/wallet-query";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumInputSeparated } from "@/components/ui/num-input-separated";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency, formatKebabCase } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { usePaystackStore } from "@/store/paystack";
import { useSafeHaven } from "@/store/safehaven";
import { useVPaymentWidget } from "@/store/vfd";
import { PaymentProvider, TransactionType } from "@/types/banks";
import env from "@/valid-env";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

const depositSchema = z.object({
	amount: z.coerce.string({
		required_error: "Price is required"
	}).refine(val => !isNaN(Number(val)) && Number(val) > 0, {
		message: "Price must be a number greater than 0"
	}),
	description: z.string().nonempty("Description is required")
});

const DEPOSIT_OPTIONS = [
	5000,
	10000,
	15000,
	20000,
	30000,
	40000,
	50000,
	100000,
	200000,
	500000
];

function useDepositPaymentTranx() {
	const safeHaven = useSafeHaven();
	const paystack = usePaystackStore();
	const updateTranx = useUpdateTransaction();
	const queryClient = useQueryClient();
	const vfd = useVPaymentWidget();

	const verifyVfdSession = useVerifyVfdSession();

	const user = useAuthStore(state => state.user);

	const deposit = useCreateDesposit(async (data, vars) => {
		if (!user)
			throw new Error("User not found");
		switch (vars.paymentProvider) {
			case PaymentProvider.PAYSTACK:
				paystack.initializePayment({
					amount: vars.amount * 100,
					email: user?.email ?? "",
					reference: data?.data.paymentConfig.reference ?? "",
					key: data?.data.paymentConfig.publicKey ?? "",
					metadata: {
						custom_fields: [
							{
								display_name: user?.fullName ?? "",
								value: user?.fullName ?? "",
								variable_name: user?.fullName ?? ""
							}
						]
					},
					onSuccess() {
						updateTranx.mutateAsync({ transactionId: data.data.deposit.id });
					},
					onError() {
						toast.error("Payment failed");
					},
					onCancel() {
						toast.warning("Payment cancelled");
					}
				});

				break;
			case PaymentProvider.VFD:
				vfd.initializePayment({
					amount: vars.amount,
					environment: env.VITE_VFD_ENV,
					token: data.data.paymentConfig.accessToken ?? "",
					merchantId: data.data.paymentConfig.merchantId ?? "",
					onSuccess: async (response) => {
						verifyVfdSession.mutateAsync({
							type: "TRANSACTION",
							transactionId: data.data.deposit.id,
							sessionId: response.reference
						}).then(() => {
							updateTranx.mutateAsync({ transactionId: data.data.deposit.id });
						});
						queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
					},
					onClose() {
						toast.warning("Payment cancelled");
					}
				});

				break;
			case PaymentProvider.SAFEHAVEN:
				if (!user)
					throw new Error("User not found");
				await safeHaven.initializePayment({
					customer: {
						firstName: user.firstName,
						lastName: user.lastName,
						emailAddress: user.email,
						phoneNumber: user.phoneNumber ?? "+234 723 432 3434"
					},
					clientId: data?.data.paymentConfig.clientId ?? "",
					settlementAccount:
						{
							accountNumber: data.data.paymentConfig.settlementAccount.accountNumber ?? "",
							bankCode: data.data.paymentConfig.settlementAccount.bankCode ?? ""
						},
					amount: vars.amount,
					referenceCode: data?.data.paymentConfig.reference ?? "",
					callback() {
						updateTranx.mutateAsync({ transactionId: data.data.deposit.id });
						queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
					}
				});
				break;

			default:
				throw new Error("Invalid payment provider");
		}
	}

	);

	const depositInit = async (v: {
		amount: number;
		description: TransactionType;
		paymentProvider: PaymentProvider;
	}) => {
		if (!user)
			throw new Error("User not found");
		await deposit.mutateAsync({
			amount: Number(v.amount),
			description: v.description,
			paymentProvider: v.paymentProvider
		});
	};

	return {
		query: deposit,
		depositInit
	};
}

const paymentSchema = z.object({
	provider: z.nativeEnum(PaymentProvider)
});

export function Deposit() {
	const [open, setOpen] = useState(false);
	const deposit = useDepositPaymentTranx();
	const [showOther, setShowOther] = useState(false);

	const [step, setStep] = useState<"deposit" | "method">("deposit");
	const depositForm = useForm<z.infer<typeof depositSchema>>({
		resolver: zodResolver(depositSchema),
		defaultValues: {
			amount: "",
			description: ""
		}
	});

	const { data: paymentMethods } = usePaymentMethods();

	const paymentForm = useForm<z.infer<typeof paymentSchema>>({
		resolver: zodResolver(paymentSchema),
		defaultValues: {
			provider: undefined
		}
	});
	const handleDepositSubmit = async () => {
		setStep("method");
	};

	const handleSubmit = async (v: z.infer<typeof paymentSchema>) => {
		try {
			await deposit.depositInit({
				amount: Number(depositForm.getValues().amount),
				description: TransactionType.DEPOSIT_FUND,
				paymentProvider: v.provider
			});
			setOpen(false);
			setStep("deposit");
			depositForm.reset();
		}

		catch (error) {
			void error;
			toast.error("Something went wrong");
		}
	};

	const handleOpenChange = (newOpenState: boolean) => {
		setOpen(newOpenState);
		if (!newOpenState)
			depositForm.reset();
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button className="gap-2 border border-neutral-300" variant="secondary">
					<Plus className="w-4 h-4 mr-2" />
					Deposit
				</Button>
			</DialogTrigger>
			{step === "deposit" && (
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle>Top up</DialogTitle>
						<DialogDescription>
							How much cash do you want to add to your wallet?
						</DialogDescription>
					</DialogHeader>
					<div className="grid grid-cols-2 gap-2 w-full">
						{!showOther && DEPOSIT_OPTIONS.map(option => (
							<Button
								key={option}
								variant={Number(depositForm.watch("amount")) !== option ? "outline" : "accent"}
								onClick={() => {
									depositForm.setValue("amount", String(option));
								}}
							>
								{formatCurrency(option, "NGN")}
							</Button>
						))}

						{!showOther && (
							<Button
								variant="outline"
								onClick={() => setShowOther(!showOther)}
							>
								Other
							</Button>
						)}
					</div>
					<div className="grid gap-4 py-4">
						<Form {...depositForm}>
							<form onSubmit={depositForm.handleSubmit(handleDepositSubmit)} className="space-y-4">
								<div className="flex flex-col items-start justify-center space-y-2">

									{showOther && (
										<FormField

											control={depositForm.control}
											name="amount"
											render={({ field }) => (
												<FormItem
													className="w-full"
												>
													<FormControl>
														<div className="*:not-first:mt-2">
															<Label htmlFor={field.name} className="sr-only">Enter Amount</Label>
															<NumInputSeparated
																label=""
																currency="NGN"
																value={field.value}
																onChange={field.onChange}
															/>
														</div>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)}
									<FormField
										control={depositForm.control}
										name="description"
										render={({ field }) => (
											<FormItem className="w-full">
												<FormControl>
													<Input
														placeholder="Enter Remark"
														{...field}
													/>

												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

								</div>
							</form>
						</Form>
					</div>
					<DialogFooter>
						{showOther && (
							<Button
								className="w-full"
								variant="secondary"
								type="submit"
								onClick={() => {
									setShowOther(false);
									depositForm.clearErrors();
								}}
							>
								Back
							</Button>
						)}
						<Button
							className="w-full"
							disabled={deposit.query.isPending}
							type="submit"
							onClick={depositForm.handleSubmit(handleDepositSubmit)}
						>
							{deposit.query.isPending
								? (
										<p className="space-x-2 flex items-center justify-center">
											<Loader2 size={20} className="animate-spin" />
											<span>Loading...</span>
										</p>
									)
								: "Continue"}
						</Button>
					</DialogFooter>
				</DialogContent>
			)}

			{step === "method" && (
				<DialogContent>
					<>
						<DialogHeader className="flex-row items-start justify-between space-y-0">
							<DialogTitle>Choose a payment method</DialogTitle>
						</DialogHeader>
						<DialogDescription className="sr-only">
							Choose a payment method
						</DialogDescription>
						<Form {...paymentForm}>
							<form onSubmit={paymentForm.handleSubmit(handleSubmit)} className="space-y-4">
								<FormField
									control={paymentForm.control}
									name="provider"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Select onValueChange={field.onChange} value={field.value}>
													<SelectTrigger id="provider" className="w-full">
														<SelectValue placeholder="Select a payment option" />
													</SelectTrigger>
													<SelectContent>
														{paymentMethods?.data.data?.map((method: { id: string; name: string }) => {
															return (
																<SelectItem className="capitalize" key={method.id} value={method.name}>{formatKebabCase(method.name)}</SelectItem>
															);
														})}
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<DialogFooter>
									<Button disabled={deposit.query.isPending} type="submit" className="w-full">
										{deposit.query.isPending
											? (
													<>
														<Loader2 size={20} className="animate-spin" />
														{" "}
															&nbsp; Loading....
													</>
												)
											: (
													"Continue"
												)}
									</Button>
								</DialogFooter>
							</form>
						</Form>
					</>
				</DialogContent>
			)}
		</Dialog>
	);
}

export default function InputWithCurrency() {
	const id = useId();
	return (
		<div className="*:not-first:mt-2">
			<Label htmlFor={id} className="sr-only">Enter Amount</Label>
			<div className="relative">
				<Input
					id={id}
					className="peer ps-6 pe-12"
					placeholder="0.00"
					type="text"
				/>
				<span className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-sm peer-disabled:opacity-50">
					₦
				</span>
				<span className="text-muted-foreground pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-sm peer-disabled:opacity-50">
					NGN
				</span>
			</div>
		</div>
	);
}
